import { type MouseEvent, useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Card, Stack, Button, LinearProgress, Typography, Grid } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

import { useRole } from '../../../hooks/useRole';
import { usePermissions } from '../../../hooks/usePermissions';
import { BulkRequest, Request, Test } from '../../../models';
import { Payment, PaymentOptionSelector } from '../../../components/bulk-request';
import { Note, Invoices, TestReview, AssessmentDetail, RequestReports, TotalSummary } from '../../../components/shared';
import Toast from '../../../components/toast';
import SuccessAlert from '../../../components/alert';
import { Status } from '../../../components/request';
import InfoCard from '../../../components/info-card';
import { fCurrency } from '../../../utils/formatNumber';
import { getFileMime } from '../../../utils/config';
import { omit } from '../../../utils/helpers';
import {
	useGetBulkRequestQuery,
	usePayBulkRequestMutation,
	useGetBulkRequestPaymentSummaryQuery,
	useGetBulkRequestReportsQuery as useGetRequestReportsQuery,
	useLazyGetBulkRequestReportQuery as useLazyGetRequestReportQuery,
	useUploadBulkRequestReportMutation as useUploadRequestReportMutation,
	useDeleteBulkRequestReportMutation as useDeleteRequestReportMutation,
	useSetBulkRequestAdminNoteMutation,
	useSetBulkRequestAccountNoteMutation,
} from '../../../slices/apis/bulkRequestApi';
import { useGenerateBulkPrebillingInvoiceMutation } from '../../../slices/apis/bulkRequestApi';
import { useRequestRules } from '../../../hooks/useRequestRules';
import Iconify from '../../../components/iconify';
import { BreadCrumbs } from '../../../components/nav-section';
import { fDateTime } from '../../../utils/formatTime';

function calculateActualTotal(totalWithoutGst: number, discountedAmount: number): number {
	const totalWithoutGstCents = Math.round(totalWithoutGst * 100);
	const discountedAmountCents = Math.round(discountedAmount * 100);

	const paidWithoutGstCents = totalWithoutGstCents - discountedAmountCents;

	const gstCents = Math.round((paidWithoutGstCents * 10) / 100);

	const paidAmountCents = paidWithoutGstCents + gstCents;

	return paidAmountCents / 100;
}

function calculateGST(totalWithGst: number) {
	const totalCents = Math.round(totalWithGst * 100);
	return Math.round(totalCents / 11) / 100;
}

const BulkRequestView = () => {
	const navigate = useNavigate();
	const { isUser, isAdmin, isSupplier, isContractor } = useRole();
	const { can } = usePermissions();
	const { reqId } = useParams() as { reqId: string };
	const [amountToPay, setAmountToPay] = useState<number>(0);
	const [invoiceSentAlertOpen, setInvoiceSentAlertOpen] = useState(false);

	const { data } = useGetBulkRequestQuery(reqId as string);
	const [payBulkRequest, { isSuccess, data: payBulkRequestData }] = usePayBulkRequestMutation();
	const [setBulkRequestAdminNote, { isSuccess: isUpdateAdminNoteSuccess, isError: isUpdateAdminNoteError }] =
		useSetBulkRequestAdminNoteMutation();
	const [setBulkRequestAccountNote, { isSuccess: isUpdateAccountNoteSuccess, isError: isUpdateAccountNoteError }] =
		useSetBulkRequestAccountNoteMutation();
	const { data: paymentSummary } = useGetBulkRequestPaymentSummaryQuery(reqId, { skip: !can('req:read:payment') });
	const { allowPayment } = useRequestRules({ data, paymentSummary });
	const [
		generatePrebillingInvoice,
		{ isLoading: isGeneratingInvoice, isSuccess: isInvoiceSuccess, isError: isInvoiceError, data: invoiceData },
	] = useGenerateBulkPrebillingInvoiceMutation();

	useEffect(() => {
		if (isSuccess && payBulkRequestData) {
			window.location.href = payBulkRequestData.url;
		}
	}, [isSuccess, payBulkRequestData]);

	useEffect(() => {
		if (isInvoiceSuccess && invoiceData?.invoiceEmailStatus === 'SENT') {
			setInvoiceSentAlertOpen(true);
		}
	}, [isInvoiceSuccess, invoiceData]);

	useEffect(() => {
		if (paymentSummary?.remaining !== undefined) {
			setAmountToPay(paymentSummary.remainingWithoutGST);
		}
	}, [paymentSummary]);

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	if (!data || (can('req:read:payment') && !paymentSummary)) {
		return <LinearProgress />;
	}

	const onPayRequest = () => {
		payBulkRequest({
			id: Number(reqId),
			amount: amountToPay,
		});
	};

	const onGeneratePrebillingInvoice = () => {
		generatePrebillingInvoice(Number(reqId));
	};

	const handleInvoiceSentAlertClose = () => {
		setInvoiceSentAlertOpen(false);
	};

	const handlePaymentChange = (amount: number) => {
		setAmountToPay(amount);
	};

	const handleAdminNoteSave = (note: string) => {
		setBulkRequestAdminNote({
			id: reqId,
			note,
		});
	};

	const handleAccountNoteSave = (note: string) => {
		setBulkRequestAccountNote({
			id: reqId,
			note,
		});
	};

	let mimeTypes = '';

	if (isUser) {
		mimeTypes = getFileMime('XLSX');
	} else if (isAdmin) {
		mimeTypes = getFileMime();
	}

	const scheduleDateLineText = BulkRequest.isAssessmentDatesSet(data.assessmentDates) ? 'Scheduled' : 'Preferred';
	const scheduleDates = BulkRequest.isAssessmentDatesSet(data.assessmentDates)
		? data.assessmentDates
		: data.prefAssessmentDates;

	const totalWithDiscount = paymentSummary
		? calculateActualTotal(paymentSummary.totalWithoutGst, paymentSummary.discountedAmount)
		: 0;

	const breadCrumbItems = [
		{
			text: 'My Bulk Requests',
			onClick: handleNavigate,
			dataRoute: '/dashboard/bulk-requests',
		},
		{
			text: `Request ${data.id}`,
		},
	];

	const assessmentDateSubHeader = (
		<Box sx={{ mb: 4 }}>
			<Stack spacing={1} alignItems="flex-start">
				<Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<BreadCrumbs items={breadCrumbItems} />
				</Box>
				<Status status={data.status} />
			</Stack>
		</Box>
	);

	return (
		<>
			<Helmet>
				<title> Bulk Request Detail - {process.env.REACT_APP_ORG} </title>
			</Helmet>

			{isUpdateAdminNoteSuccess && (
				<Toast body={'Admin note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAdminNoteError && (
				<Toast body={'Admin note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteSuccess && (
				<Toast body={'Account note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteError && (
				<Toast body={'Account note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{(isInvoiceError || (isInvoiceSuccess && invoiceData?.invoiceEmailStatus === 'FAILED')) && (
				<Toast body={'Failed to send payable invoice.'} severity="error" autoHideDuration={3000} />
			)}

			<SuccessAlert
				open={invoiceSentAlertOpen}
				title={'Invoice email sent'}
				subtitle={'Payable invoice has been emailed to the requesting user.'}
				onClose={handleInvoiceSentAlertClose}
			/>

			<Box sx={{ p: 4 }}>
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 7 }}>
						<Stack spacing={3}>
							<InfoCard title="Item Details" header={assessmentDateSubHeader}>
								<TestReview
									tests={Test.applyDiscount(data.tests, paymentSummary?.bulkTestDiscounts.discountedTests ?? []).map(
										(test) => (isSupplier || isContractor ? omit(test, ['price']) : test),
									)}
								/>
							</InfoCard>
							<InfoCard title="Assessment Details">
								<AssessmentDetail
									user={data.user}
									assessmentDates={scheduleDates}
									location={data.location}
									dateLineText={scheduleDateLineText}
								/>
								{data.status?.toLowerCase() === 'cancelled' && (
									<Stack direction="row" spacing={0.4} alignItems="center">
										<Iconify icon="eva:alert-triangle-outline" sx={{ color: 'error.main' }} />
										<Typography color="error">
											This request has been cancelled at {fDateTime(data.updatedAt)}
										</Typography>
									</Stack>
								)}
							</InfoCard>
							{paymentSummary && (
								<Payment>
									<Box display="flex" justifyContent="space-between" mb={1}>
										<Typography variant="subtitle1">Amount Paid:</Typography>
										<Typography variant="subtitle1">{fCurrency(paymentSummary.paidAmount)}</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between" mb={1}>
										<Typography variant="subtitle1">Remaining Balance:</Typography>
										<Typography variant="subtitle1">{fCurrency(paymentSummary.remaining)}</Typography>
									</Box>
									<Box>
										<Typography variant="subtitle2" color="error">
											* Note all amounts have GST included.
										</Typography>
									</Box>
									{allowPayment && (
										<>
											{/* no further deposit is allowed after initial one. Outstanding balance must be paid in full amount */}
											{paymentSummary.paidAmount === 0 && (
												<PaymentOptionSelector
													tests={Test.applyDiscount(data.tests, paymentSummary.bulkTestDiscounts.discountedTests)}
													onChange={handlePaymentChange}
												/>
											)}
											<Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
												{can('breq:invoice') && data.paymentStatus === Request.paymentStatus.unpaid && (
													<Button
														variant="contained"
														loading={isGeneratingInvoice}
														onClick={onGeneratePrebillingInvoice}
													>
														Send Payable Invoice
													</Button>
												)}
												<Button variant="contained" color="brand" disabled={isSuccess} onClick={onPayRequest}>
													Pay Now
												</Button>
											</Box>
										</>
									)}
								</Payment>
							)}
						</Stack>
					</Grid>

					<Grid size={{ xs: 12, md: 5 }}>
						<Stack spacing={3}>
							{data.tests && paymentSummary && (
								<InfoCard title="Summary">
									<TotalSummary
										total={totalWithDiscount}
										subtotal={paymentSummary.totalWithoutGst}
										gst={calculateGST(totalWithDiscount)}
										discount={paymentSummary.discountedAmount}
										discountLabel={data.paymentStatus === 'DEPOSIT_PAID' ? 'Discount (on deposit)' : undefined}
									/>
								</InfoCard>
							)}
							{paymentSummary && paymentSummary.transactions.length > 0 && (
								<InfoCard title="Invoices">
									<Invoices transactions={paymentSummary.transactions} />
								</InfoCard>
							)}
							{data.id && (
								<Card>
									<RequestReports
										requestId={Number(data.id)}
										apis={{
											useGetRequestReportsQuery,
											useLazyGetRequestReportQuery,
											useUploadRequestReportMutation,
											useDeleteRequestReportMutation,
										}}
										mimeTypes={mimeTypes}
										isBulkRequest
									/>
								</Card>
							)}
							{can('req:note:read') && (
								<InfoCard title="Admin Note">
									<Note
										title="Admin Note"
										val={data.adminNote}
										onSave={can('breq:update:notes') ? handleAdminNoteSave : undefined}
									/>
								</InfoCard>
							)}
							{can('req:note:read') && (
								<InfoCard title="Account Note">
									<Note
										title="Account Note"
										val={data.accountNote}
										onSave={can('breq:update:notes') ? handleAccountNoteSave : undefined}
									/>
								</InfoCard>
							)}
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default BulkRequestView;
